import React, { useState, useEffect } from 'react';
import { 
  Settings, CheckCircle, XCircle, ChevronDown, ChevronUp, 
  ExternalLink, Eye, EyeOff, Save, Trash2, Power, PowerOff,
  AlertTriangle, HelpCircle, Link2, Shield, RefreshCw,
  ToggleLeft, ToggleRight, Copy, Check
} from 'lucide-react';
import apiClient from '../api/client';
import { erpMappingsAPI } from '../api/client';

const PROVIDER_ICONS = {
  dynamics_bc: '🏢',
  quickbooks: '📗',
  xero: '🔵',
  sap_s4hana: '🔷',
  sap_b1: '🟦',
};

const PROVIDER_FEATURES = {
  dynamics_bc: [
    { id: 'bc_oauth_token',         method: 'POST',  name: 'OAuth2 Token Acquisition',  description: 'Authenticate with Azure AD using client credentials',    defaultEnabled: true },
    { id: 'bc_get_company',         method: 'GET',   name: 'Resolve Company',            description: 'Look up BC company ID by name',                          defaultEnabled: true },
    { id: 'bc_get_vendors',         method: 'GET',   name: 'Fetch Vendors',              description: 'Check if vendor exists before creating',                 defaultEnabled: true },
    { id: 'bc_create_vendor',       method: 'POST',  name: 'Create Vendor',              description: 'Auto-create vendor record in Business Central',           defaultEnabled: true },
    { id: 'bc_update_vendor',       method: 'PATCH', name: 'Update Vendor',              description: 'Sync vendor changes to Business Central',                defaultEnabled: true },
    { id: 'bc_get_gl_accounts',     method: 'GET',   name: 'Fetch GL Accounts',          description: 'Validate GL account codes during posting',                defaultEnabled: true },
    { id: 'bc_get_purchase_orders',   method: 'GET',   name: 'Fetch Purchase Orders',       description: 'Pull open POs for invoice matching',                         defaultEnabled: true },
    { id: 'bc_create_purchase_order', method: 'POST',  name: 'Create Purchase Order',       description: 'Push a new purchase order from SpocoDoc into Business Central', defaultEnabled: false },
    { id: 'bc_update_purchase_order', method: 'PATCH', name: 'Update Purchase Order',       description: 'Sync purchase order changes back to Business Central',        defaultEnabled: false },
    { id: 'bc_create_invoice',        method: 'POST',  name: 'Create Purchase Invoice',     description: 'Post approved invoice as BC purchase invoice',               defaultEnabled: true },
    { id: 'bc_submit_invoice',      method: 'POST',  name: 'Submit Invoice (NAV.post)',  description: 'Mark invoice as Posted in Business Central',              defaultEnabled: true },
  ],
  xero: [
    { id: 'xero_oauth_exchange',  method: 'POST',  name: 'OAuth2 Token Exchange',    description: 'Exchange authorization code for access token',             defaultEnabled: true },
    { id: 'xero_token_refresh',   method: 'POST',  name: 'Refresh Access Token',     description: 'Auto-refresh expired tokens before API calls',             defaultEnabled: true },
    { id: 'xero_get_tenants',     method: 'GET',   name: 'Get Connections',          description: 'Retrieve the authorized Xero organisation',                defaultEnabled: true },
    { id: 'xero_get_accounts',    method: 'GET',   name: 'Fetch Accounts',           description: 'Get chart of accounts for GL code mapping',                defaultEnabled: true },
    { id: 'xero_get_contacts',    method: 'GET',   name: 'Fetch Contacts',           description: 'Look up vendor as a Xero contact',                         defaultEnabled: true },
    { id: 'xero_create_contact',  method: 'POST',  name: 'Create / Update Contact',  description: 'Sync vendor details as a Xero contact',                    defaultEnabled: true },
    { id: 'xero_get_invoices',    method: 'GET',   name: 'Fetch Invoices',           description: 'Check for existing bills to avoid duplicates',             defaultEnabled: true },
    { id: 'xero_create_invoice',  method: 'POST',  name: 'Create Bill (ACCPAY)',     description: 'Post approved invoice as a Xero bill',                     defaultEnabled: true },
    { id: 'xero_update_invoice',  method: 'POST',  name: 'Update Bill',              description: 'Re-sync invoice changes back to Xero',                     defaultEnabled: true },
    { id: 'xero_get_po',          method: 'GET',   name: 'Fetch Purchase Orders',     description: 'Pull open POs for matching',                               defaultEnabled: true },
    { id: 'xero_create_po',        method: 'POST',  name: 'Create Purchase Order',     description: 'Push a new purchase order from SpocoDoc into Xero',        defaultEnabled: false },
    { id: 'xero_update_po',        method: 'POST',  name: 'Update Purchase Order',     description: 'Sync purchase order changes back to Xero',                 defaultEnabled: false },
  ],
};

export default function FinanceIntegrationSettings() {
  const [providers, setProviders] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [activeIntegration, setActiveIntegration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Selected provider for configuration
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [formFields, setFormFields] = useState({});
  const [showGuide, setShowGuide] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [activateOnSave, setActivateOnSave] = useState(true);
  const [featureToggles, setFeatureToggles] = useState({});
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [configTab, setConfigTab] = useState('config');

  // ERP Mapping tab state
  const [mappingEntityType, setMappingEntityType] = useState('gl_account');
  const [mappings, setMappings] = useState([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [editingMapping, setEditingMapping] = useState({}); // { [id]: externalCode }
  const [mappingsSaving, setMappingsSaving] = useState({});

  const copyToClipboard = (key, value) => {
    navigator.clipboard.writeText(value || '');
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 2000);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle Xero OAuth callback redirect (?xero=success|error&message=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const xero = params.get('xero');
    const message = params.get('message');
    if (xero) {
      if (xero === 'success') {
        alert(message || 'Xero connected successfully!');
      } else {
        alert(`Xero connection failed: ${message || 'Unknown error'}`);
      }
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      // Refresh integrations to show new connection state
      fetchData();
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/finance-integrations');
      setIntegrations(res.data.integrations || []);
      setActiveIntegration(res.data.activeIntegration);
      setProviders(res.data.providers || []);
    } catch (err) {
      console.error('Failed to load integrations:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectProvider = (provider) => {
    setSelectedProvider(provider);
    setShowGuide(false);
    setTestResult(null);
    setFeatureToggles({});
    setConfigTab('config');

    // Pre-fill form if this provider already has a saved config
    const existing = integrations.find(i => i.Provider === provider.id);
    if (existing) {
      const fields = {};
      provider.fields.forEach(f => {
        fields[f.key] = existing[f.key] || '';
      });
      setFormFields(fields);
      setActivateOnSave(existing.IsActive);
      if (['dynamics_bc', 'xero'].includes(provider.id)) {
        loadFeatures(existing.IntegrationID);
      }
    } else {
      const fields = {};
      provider.fields.forEach(f => {
        fields[f.key] = f.options ? f.options[0]?.value || '' : '';
      });
      setFormFields(fields);
      setActivateOnSave(true);
    }
  };

  const loadFeatures = async (integrationId) => {
    setFeaturesLoading(true);
    try {
      const res = await apiClient.get(`/finance-integrations/${integrationId}/features`);
      const toggles = {};
      (res.data.features || []).forEach(f => { toggles[f.id] = f.enabled; });
      setFeatureToggles(toggles);
    } catch (err) {
      console.error('Failed to load features:', err);
    } finally {
      setFeaturesLoading(false);
    }
  };

  const handleFeatureToggle = async (featureId) => {
    const existing = integrations.find(i => i.Provider === selectedProvider?.id);
    if (!existing) return;
    const current = featureToggles[featureId] !== undefined
      ? featureToggles[featureId]
      : (PROVIDER_FEATURES[selectedProvider.id]?.find(f => f.id === featureId)?.defaultEnabled ?? true);
    const newToggles = { ...featureToggles, [featureId]: !current };
    setFeatureToggles(newToggles);
    try {
      await apiClient.put(`/finance-integrations/${existing.IntegrationID}/features`, { features: newToggles });
    } catch (err) {
      setFeatureToggles(featureToggles);
      console.error('Failed to save features:', err);
    }
  };

  const handleFieldChange = (key, value) => {
    setFormFields(prev => ({ ...prev, [key]: value }));
  };

  const loadMappings = async (provider, entityType) => {
    setMappingsLoading(true);
    setMappings([]);
    setEditingMapping({});
    try {
      const res = await erpMappingsAPI.getAll({ provider, entityType });
      setMappings(res.data?.mappings || []);
    } catch (e) {
      console.error('loadMappings error:', e.message);
    } finally {
      setMappingsLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.post('/finance-integrations', {
        provider: selectedProvider.id,
        fields: formFields,
        activate: activateOnSave,
      });
      alert(`${selectedProvider.shortName} integration saved successfully!`);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await apiClient.post('/finance-integrations/test', {
        provider: selectedProvider.id,
        fields: formFields,
      });
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.message || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleActivate = async (integrationId) => {
    try {
      await apiClient.put(`/finance-integrations/${integrationId}/activate`);
      await fetchData();
    } catch (err) {
      alert('Failed to activate integration');
    }
  };

  const handleDeactivate = async (integrationId) => {
    try {
      await apiClient.put(`/finance-integrations/${integrationId}/deactivate`);
      await fetchData();
    } catch (err) {
      alert('Failed to deactivate integration');
    }
  };

  const handleConnectXero = async () => {
    try {
      const existing = integrations.find(i => i.Provider === 'xero');
      if (!existing) {
        alert('Please save Client ID and Client Secret first.');
        return;
      }
      const res = await apiClient.get('/finance-integrations/connect/xero');
      if (res.data?.authorizeUrl) {
        window.location.href = res.data.authorizeUrl;
      } else {
        alert('Could not start Xero authorization.');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to start Xero authorization');
    }
  };

  const handleDelete = async (integrationId) => {
    if (!confirm('Are you sure you want to remove this integration configuration?')) return;
    try {
      await apiClient.delete(`/finance-integrations/${integrationId}`);
      setSelectedProvider(null);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete integration');
    }
  };

  const toggleSecret = (key) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mr-2" />
        <span>Loading integrations...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Link2 className="w-7 h-7 text-blue-600" />
            Finance Integrations
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Connect to your finance tool to post approved invoices. Only one integration can be active at a time.
          </p>
        </div>
        {activeIntegration && (
          <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div className="text-sm">
              <span className="text-green-800 font-medium">Active: </span>
              <span className="text-green-700">{activeIntegration.DisplayName}</span>
            </div>
          </div>
        )}
      </div>

      {/* Provider Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {providers.map(provider => {
          const existing = integrations.find(i => i.Provider === provider.id);
          const isActive = existing?.IsActive;
          const isSelected = selectedProvider?.id === provider.id;

          return (
            <button
              key={provider.id}
              onClick={() => selectProvider(provider)}
              className={`relative text-left p-5 rounded-xl border-2 transition-all ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                  : isActive
                    ? 'border-green-400 bg-green-50/50 hover:border-green-500'
                    : existing
                      ? 'border-gray-300 bg-gray-50 hover:border-blue-300'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/30'
              }`}
            >
              {/* Status badge */}
              {isActive && (
                <span className="absolute top-3 right-3 flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Active
                </span>
              )}
              {existing && !isActive && (
                <span className="absolute top-3 right-3 text-xs font-medium text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                  Configured
                </span>
              )}

              <div className="text-3xl mb-2">{provider.logo}</div>
              <h3 className="font-semibold text-gray-900">{provider.shortName}</h3>
              <p className="text-xs text-gray-500 mt-1">{provider.name}</p>

              {existing?.LastSyncAt && (
                <p className="text-xs text-gray-400 mt-2">
                  Last sync: {new Date(existing.LastSyncAt).toLocaleDateString('en-GB')}
                  {existing.LastSyncStatus && (
                    <span className={`ml-1 ${
                      existing.LastSyncStatus === 'Success' ? 'text-green-600' :
                      existing.LastSyncStatus === 'Failed' ? 'text-red-600' : 'text-amber-600'
                    }`}>
                      ({existing.LastSyncStatus})
                    </span>
                  )}
                </p>
              )}
            </button>
          );
        })}
      </div>

      {/* Configuration Panel */}
      {selectedProvider && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Panel header */}
          <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{selectedProvider.logo}</span>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Configure {selectedProvider.shortName}
                </h2>
                <p className="text-sm text-gray-500">
                  Enter the connection details for {selectedProvider.name}
                </p>
              </div>
            </div>
            {configTab === 'config' && (
              <button
                onClick={() => setShowGuide(!showGuide)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <HelpCircle className="w-4 h-4" />
                {showGuide ? 'Hide Guide' : 'Setup Guide'}
              </button>
            )}
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 bg-white px-6">
            <button
              onClick={() => setConfigTab('config')}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors -mb-px ${
                configTab === 'config'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Configuration
            </button>
            {['xero', 'dynamics_bc'].includes(selectedProvider.id) && (
              <button
                onClick={() => setConfigTab('webhooks')}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors -mb-px flex items-center gap-1.5 ${
                  configTab === 'webhooks'
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Link2 className="w-3.5 h-3.5" />
                Webhooks
              </button>
            )}
            {integrations.find(i => i.Provider === selectedProvider.id && i.IsActive) && (
              <button
                onClick={() => {
                  setConfigTab('mapping');
                  loadMappings(selectedProvider.id, mappingEntityType);
                }}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors -mb-px flex items-center gap-1.5 ${
                  configTab === 'mapping'
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Mapping
              </button>
            )}
          </div>

          {/* Setup Guide (collapsible, config tab only) */}
          {configTab === 'config' && showGuide && selectedProvider.guide && (
            <div className="px-6 py-5 bg-blue-50/50 border-b border-blue-100">
              <h3 className="text-md font-semibold text-blue-900 mb-4">{selectedProvider.guide.title}</h3>
              <div className="space-y-4">
                {selectedProvider.guide.steps.map((step, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-4 border border-blue-100">
                    <h4 className="font-medium text-blue-800 mb-1">{step.heading}</h4>
                    <p className="text-sm text-gray-600">{step.content}</p>
                  </div>
                ))}
              </div>
              {selectedProvider.guide.links?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-3">
                  {selectedProvider.guide.links.map((link, idx) => (
                    <a
                      key={idx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-700 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Configuration Tab ─────────────────────────────────────── */}
          {configTab === 'config' && (
          <div className="px-6 py-5 space-y-4">
            {selectedProvider.fields.filter(f => !f.webhookOnly).map(field => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {field.type === 'readonly' ? (
                  <div className="relative">
                    <input
                      type="text"
                      value={field.value || ''}
                      readOnly
                      className="w-full border border-blue-200 bg-blue-50 rounded-lg px-4 py-2.5 text-sm text-blue-800 font-mono pr-10 cursor-default select-all"
                    />
                    <button
                      type="button"
                      onClick={() => copyToClipboard(field.key, field.value)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-400 hover:text-blue-600"
                      title="Copy to clipboard"
                    >
                      {copiedField === field.key
                        ? <Check className="w-4 h-4 text-green-500" />
                        : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                ) : field.type === 'select' ? (
                  <select
                    value={formFields[field.key] || ''}
                    onChange={e => handleFieldChange(field.key, e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
                  >
                    {field.options?.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <div className="relative">
                    <input
                      type={field.type === 'password' && !showSecrets[field.key] ? 'password' : 'text'}
                      value={formFields[field.key] || ''}
                      onChange={e => handleFieldChange(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400 pr-10"
                    />
                    {field.type === 'password' && (
                      <button
                        type="button"
                        onClick={() => toggleSecret(field.key)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showSecrets[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                )}
                {field.hint && (
                  <p className="mt-1 text-xs text-gray-500">{field.hint}</p>
                )}
              </div>
            ))}

            {/* Xero OAuth status & Connect button */}
            {selectedProvider.id === 'xero' && (() => {
              const existing = integrations.find(i => i.Provider === 'xero');
              const isConnected = existing?.IsConnected;
              const tenantName = existing?.XeroTenantName;
              const tenantId = existing?.XeroTenantId;
              return (
                <div className={`mt-2 p-4 rounded-lg border ${isConnected ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                      {isConnected
                        ? <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                        : <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />}
                      <div>
                        <p className={`text-sm font-medium ${isConnected ? 'text-green-800' : 'text-amber-800'}`}>
                          {isConnected ? 'Xero is connected' : 'Xero is not connected yet'}
                        </p>
                        {isConnected && (tenantName || tenantId) && (
                          <div className="mt-2 text-xs space-y-0.5">
                            {tenantName && (
                              <p className="text-gray-800">
                                <span className="font-medium">Connected organisation:</span> {tenantName}
                              </p>
                            )}
                            {tenantId && (
                              <p className="text-gray-500 font-mono">
                                <span className="font-sans font-medium text-gray-600">Tenant ID:</span> {tenantId}
                              </p>
                            )}
                          </div>
                        )}
                        <p className="text-xs text-gray-600 mt-1">
                          {isConnected
                            ? 'You can now import GL accounts, sync vendors, and post invoices to Xero.'
                            : 'After saving Client ID and Client Secret, click "Connect to Xero" to authorize the app and obtain access tokens.'}
                        </p>
                      </div>
                    </div>
                    {existing && (
                      <button
                        type="button"
                        onClick={handleConnectXero}
                        className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 whitespace-nowrap ${
                          isConnected
                            ? 'text-blue-700 bg-white border border-blue-300 hover:bg-blue-50'
                            : 'text-white bg-blue-600 hover:bg-blue-700'
                        }`}
                      >
                        <Link2 className="w-4 h-4" />
                        {isConnected ? 'Reconnect' : 'Connect to Xero'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Activate toggle */}
            <div className="flex items-center gap-3 pt-2">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={activateOnSave}
                  onChange={e => setActivateOnSave(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
              <span className="text-sm text-gray-700">Set as active integration on save</span>
            </div>

            {/* Test Result */}
            {testResult && (
              <div className={`flex items-start gap-2 p-3 rounded-lg border ${
                testResult.success 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                {testResult.success 
                  ? <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" /> 
                  : <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                }
                <div>
                  <p className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {testResult.message}
                  </p>
                  {testResult.issues?.map((issue, i) => (
                    <p key={i} className="text-sm text-red-600 mt-1">• {issue}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
          )} {/* end configTab === 'config' */}

          {/* ── Webhooks Tab ───────────────────────────────────────────── */}
          {configTab === 'webhooks' && (
            <div className="px-6 py-6 space-y-5">

              {/* ── XERO ── */}
              {selectedProvider.id === 'xero' && (() => {
                const urlField = selectedProvider.fields.find(f => f.key === '_webhookUrl');
                const keySet = !!integrations.find(i => i.Provider === 'xero')?.WebhookKey ||
                               !!formFields.WebhookKey;
                return (
                  <>
                    <p className="text-sm text-gray-600">
                      To receive real-time events from Xero (invoice paid, GL account changed, etc.)
                      complete both steps below. Each step is a one-time setup in the Xero Developer Portal.
                    </p>

                    {/* Step 1 — give URL to Xero */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0">1</span>
                        <h3 className="text-sm font-semibold text-blue-900">Give Xero this webhook URL</h3>
                      </div>
                      <p className="text-xs text-gray-600 mb-3">
                        In <a href="https://developer.xero.com/app/manage" target="_blank" rel="noopener noreferrer" className="underline text-blue-700">Xero Developer Portal</a> → your app → <strong>Webhooks</strong> → Create webhook.
                        Paste the URL below as the <strong>Delivery URL</strong>.
                        Then choose which event types to send (e.g. <em>Invoices</em>, <em>Accounts</em>).
                      </p>
                      <div className="relative">
                        <input
                          type="text"
                          value={urlField?.value || ''}
                          readOnly
                          className="w-full border border-blue-300 bg-white rounded-lg px-4 py-2.5 text-sm text-blue-800 font-mono pr-12 cursor-default select-all"
                        />
                        <button
                          type="button"
                          onClick={() => copyToClipboard('_webhookUrl', urlField?.value)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-400 hover:text-blue-700 flex items-center gap-1 text-xs font-medium"
                          title="Copy URL"
                        >
                          {copiedField === '_webhookUrl'
                            ? <><Check className="w-4 h-4 text-green-500" /> <span className="text-green-600">Copied</span></>
                            : <><Copy className="w-4 h-4" /> <span>Copy</span></>}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        This URL is unique to your company in SpocoDoc — Xero will only send events here.
                      </p>
                    </div>

                    {/* Step 2 — paste key from Xero */}
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-6 h-6 rounded-full bg-gray-700 text-white text-xs font-bold flex items-center justify-center shrink-0">2</span>
                        <h3 className="text-sm font-semibold text-gray-900">Paste the webhook key Xero gives you</h3>
                      </div>
                      <p className="text-xs text-gray-600 mb-3">
                        After you create the webhook in Xero, the portal shows you a <strong>webhook key</strong>.
                        Copy it and paste below. SpocoDoc uses it to verify the HMAC-SHA256 signature on every
                        incoming request — unsigned or incorrectly signed requests are rejected.
                      </p>
                      <div className="relative">
                        <input
                          type={showSecrets.WebhookKey ? 'text' : 'password'}
                          value={formFields.WebhookKey || ''}
                          onChange={e => handleFieldChange('WebhookKey', e.target.value)}
                          placeholder="Paste Xero webhook key here"
                          className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => toggleSecret('WebhookKey')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets.WebhookKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <div className="mt-3 flex items-center gap-2 text-xs">
                        {keySet
                          ? <><CheckCircle className="w-3.5 h-3.5 text-green-600" /><span className="text-green-700 font-medium">Webhook key is saved</span></>
                          : <><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /><span className="text-amber-700">No webhook key saved yet — events will not be verified</span></>}
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-1">
                      <p className="font-medium text-gray-700 mb-1">How this works:</p>
                      <p>→ Something happens in Xero (invoice paid, GL account added, etc.)</p>
                      <p>→ Xero POSTs a signed event to your webhook URL above</p>
                      <p>→ SpocoDoc verifies the signature with your key and processes the event</p>
                      <p>→ e.g. an invoice marked paid in Xero automatically updates its ERP Payment Status in SpocoDoc</p>
                    </div>
                  </>
                );
              })()}

              {/* ── DYNAMICS BC ── */}
              {selectedProvider.id === 'dynamics_bc' && (() => {
                const urlField = selectedProvider.fields.find(f => f.key === '_bcWebhookUrl');
                const customUrlField = selectedProvider.fields.find(f => f.key === 'NotificationUrl');
                return (
                  <>
                    <p className="text-sm text-gray-600">
                      Dynamics BC sends change notifications (vendor updates, payment status) to a webhook URL.
                      SpocoDoc registers subscriptions automatically when you connect — no manual Xero-style key needed.
                    </p>

                    {/* Default URL */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                      <h3 className="text-sm font-semibold text-blue-900 mb-2">Default Notification URL</h3>
                      <p className="text-xs text-gray-600 mb-3">
                        SpocoDoc uses this URL when registering BC webhook subscriptions.
                        Copy it if you need to register subscriptions manually in BC.
                      </p>
                      <div className="relative">
                        <input
                          type="text"
                          value={urlField?.value || ''}
                          readOnly
                          className="w-full border border-blue-300 bg-white rounded-lg px-4 py-2.5 text-sm text-blue-800 font-mono pr-12 cursor-default select-all"
                        />
                        <button
                          type="button"
                          onClick={() => copyToClipboard('_bcWebhookUrl', urlField?.value)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-400 hover:text-blue-700 flex items-center gap-1 text-xs font-medium"
                        >
                          {copiedField === '_bcWebhookUrl'
                            ? <><Check className="w-4 h-4 text-green-500" /> <span className="text-green-600">Copied</span></>
                            : <><Copy className="w-4 h-4" /> <span>Copy</span></>}
                        </button>
                      </div>
                    </div>

                    {/* Custom URL override */}
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">Custom URL override <span className="text-gray-400 font-normal">(optional)</span></h3>
                      <p className="text-xs text-gray-600 mb-3">
                        Only needed if your public API hostname differs from the default above.
                        Leave blank to use the default.
                      </p>
                      <input
                        type="text"
                        value={formFields.NotificationUrl || ''}
                        onChange={e => handleFieldChange('NotificationUrl', e.target.value)}
                        placeholder={customUrlField?.placeholder || ''}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
                      />
                    </div>

                    {/* How it works */}
                    <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-1">
                      <p className="font-medium text-gray-700 mb-1">How BC webhooks work:</p>
                      <p>→ SpocoDoc registers a subscription in BC when you activate the integration</p>
                      <p>→ Each subscription has a unique <strong>clientState</strong> (UUID) used to verify requests</p>
                      <p>→ BC POSTs change notifications to the URL above — SpocoDoc verifies clientState and processes events</p>
                      <p>→ Subscriptions expire every 3 days — SpocoDoc renews them automatically via a nightly scheduler</p>
                    </div>
                  </>
                );
              })()}

            </div>
          )} {/* end configTab === 'webhooks' */}

          {/* ─── Mapping Tab ─────────────────────────────────────────────── */}
          {configTab === 'mapping' && (
            <div className="px-6 py-5 space-y-4">
              {/* Sub-tab selector */}
              <div className="flex gap-2 border-b border-gray-200 pb-0">
                {[{key:'gl_account',label:'GL Accounts'},{key:'vendor',label:'Vendors'},{key:'dimension',label:'Dimensions'},{key:'currency',label:'Currencies'}].map(t => (
                  <button
                    key={t.key}
                    onClick={() => { setMappingEntityType(t.key); loadMappings(selectedProvider.id, t.key); }}
                    className={`py-2 px-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      mappingEntityType === t.key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >{t.label}</button>
                ))}
                <button
                  onClick={() => loadMappings(selectedProvider.id, mappingEntityType)}
                  className="ml-auto flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Refresh mappings"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
              </div>

              {/* Sync + auto-map button */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  {mappingEntityType === 'currency'
                    ? <>Map your SpocoDoc currency ISO codes to NetSuite’s internal numeric currency IDs.
                        Run “Sync &amp; Auto-Map” to import currencies from NetSuite — the ISO code is matched automatically.
                        Then verify the <strong>NS ID</strong> column is correct; edit any row and click Save if needed.
                        Invoices and expenses will use the mapped ID when posted.
                      </>
                    : <>Map {selectedProvider.name} codes to SpocoDoc {mappingEntityType.replace('_', ' ')} entities.
                        Highlighted rows (<span className="inline-block w-2 h-2 rounded-sm bg-amber-400 mx-1" />) have no ERP code set.
                      </>}
                </p>
                <button
                  onClick={async () => {
                    try {
                      if (mappingEntityType === 'gl_account') {
                        await apiClient.post(`/netsuite/sync/accounts`);
                      } else if (mappingEntityType === 'vendor') {
                        await apiClient.post(`/netsuite/sync/vendors`);
                      } else if (mappingEntityType === 'currency') {
                        await apiClient.post(`/netsuite/sync/currencies`);
                      }
                      await loadMappings(selectedProvider.id, mappingEntityType);
                    } catch (e) {
                      alert('Sync failed: ' + (e.response?.data?.message || e.message));
                    }
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" /> Sync &amp; Auto-Map
                </button>
              </div>

              {/* Mapping table */}
              {mappingsLoading ? (
                <div className="text-center py-8 text-gray-400 text-sm">Loading mappings…</div>
              ) : mappings.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No mappings found. Run "Sync &amp; Auto-Map" to pull records from {selectedProvider.name}.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">{mappingEntityType === 'currency' ? 'ISO Code' : 'SpocoDoc Name'}</th>
                        <th className="px-4 py-2 text-left font-medium">{mappingEntityType === 'currency' ? 'Currency Name' : 'SpocoDoc Code'}</th>
                        <th className="px-4 py-2 text-left font-medium">{mappingEntityType === 'currency' ? 'NS Internal ID' : `${selectedProvider.shortName} Code`}</th>
                        <th className="px-4 py-2 text-left font-medium">{mappingEntityType === 'currency' ? '' : `${selectedProvider.shortName} Name`}</th>
                        <th className="px-4 py-2 w-24"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {mappings.map(m => {
                        const isUnmapped = !m.ExternalCode;
                        const editVal = editingMapping[m.ERPMappingID] ?? m.ExternalCode ?? '';
                        return (
                          <tr key={m.ERPMappingID} className={isUnmapped ? 'bg-amber-50' : 'bg-white hover:bg-gray-50'}>
                            <td className="px-4 py-2 text-gray-800 font-mono text-xs">{m.InternalCode}</td>
                            <td className="px-4 py-2 text-gray-500 text-xs">{m.ExternalName || m.InternalCode}</td>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                value={editVal}
                                onChange={e => setEditingMapping(prev => ({ ...prev, [m.ERPMappingID]: e.target.value }))}
                                className={`w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-blue-500 outline-none ${
                                  isUnmapped ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
                                }`}
                                placeholder="Enter ERP code…"
                              />
                            </td>
                            <td className="px-4 py-2 text-gray-500 text-xs">{m.ExternalName}</td>
                            <td className="px-4 py-2 text-right">
                              {editingMapping[m.ERPMappingID] !== undefined && editingMapping[m.ERPMappingID] !== (m.ExternalCode ?? '') && (
                                <button
                                  disabled={mappingsSaving[m.ERPMappingID]}
                                  onClick={async () => {
                                    setMappingsSaving(p => ({ ...p, [m.ERPMappingID]: true }));
                                    try {
                                      await erpMappingsAPI.update(m.ERPMappingID, { externalCode: editingMapping[m.ERPMappingID] });
                                      setMappings(prev => prev.map(x => x.ERPMappingID === m.ERPMappingID ? { ...x, ExternalCode: editingMapping[m.ERPMappingID] } : x));
                                      setEditingMapping(p => { const n = { ...p }; delete n[m.ERPMappingID]; return n; });
                                    } catch (e) {
                                      alert('Save failed: ' + (e.response?.data?.message || e.message));
                                    } finally {
                                      setMappingsSaving(p => ({ ...p, [m.ERPMappingID]: false }));
                                    }
                                  }}
                                  className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                                >
                                  {mappingsSaving[m.ERPMappingID] ? '…' : 'Save'}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )} {/* end configTab === 'mapping' */}

          {/* Actions */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {integrations.find(i => i.Provider === selectedProvider.id) && (
                <>
                  <button
                    onClick={() => handleDelete(integrations.find(i => i.Provider === selectedProvider.id).IntegrationID)}
                    className="px-3 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove
                  </button>
                  {integrations.find(i => i.Provider === selectedProvider.id)?.IsActive ? (
                    <button
                      onClick={() => handleDeactivate(integrations.find(i => i.Provider === selectedProvider.id).IntegrationID)}
                      className="px-3 py-2 text-sm font-medium text-amber-600 bg-white border border-amber-300 rounded-lg hover:bg-amber-50 transition-colors flex items-center gap-1"
                    >
                      <PowerOff className="w-4 h-4" />
                      Deactivate
                    </button>
                  ) : (
                    <button
                      onClick={() => handleActivate(integrations.find(i => i.Provider === selectedProvider.id).IntegrationID)}
                      className="px-3 py-2 text-sm font-medium text-green-600 bg-white border border-green-300 rounded-lg hover:bg-green-50 transition-colors flex items-center gap-1"
                    >
                      <Power className="w-4 h-4" />
                      Activate
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleTest}
                disabled={testing}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                {testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                Test Connection
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:bg-gray-300"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Saving...' : 'Save Integration'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Features Panel */}
      {selectedProvider && ['dynamics_bc', 'xero'].includes(selectedProvider.id) && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">API Features</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Enable or disable individual API operations for this connector
            </p>
          </div>

          {!integrations.find(i => i.Provider === selectedProvider.id) ? (
            <div className="px-6 py-5 flex items-center gap-2 text-sm text-gray-400">
              <Settings className="w-4 h-4 opacity-50" />
              Save the integration first to manage individual features.
            </div>
          ) : featuresLoading ? (
            <div className="px-6 py-5 flex items-center gap-2 text-gray-400">
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span className="text-sm">Loading features...</span>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {(PROVIDER_FEATURES[selectedProvider.id] || []).map(feature => {
                const enabled = featureToggles[feature.id] !== undefined
                  ? featureToggles[feature.id]
                  : feature.defaultEnabled;
                return (
                  <div key={feature.id} className="px-6 py-4 flex items-center gap-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold w-14 justify-center flex-shrink-0 ${
                      feature.method === 'GET'   ? 'bg-green-100 text-green-700' :
                      feature.method === 'POST'  ? 'bg-blue-100 text-blue-700'  :
                                                   'bg-amber-100 text-amber-700'
                    }`}>
                      {feature.method}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800">{feature.name}</p>
                      <p className="text-xs text-gray-500">{feature.description}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleFeatureToggle(feature.id)}
                      className="flex-shrink-0 ml-2"
                      title={enabled ? 'Click to disable' : 'Click to enable'}
                    >
                      {enabled
                        ? <ToggleRight className="h-8 w-8 text-blue-600" />
                        : <ToggleLeft className="h-8 w-8 text-gray-300" />}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* No provider selected hint */}
      {!selectedProvider && (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Link2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-600">Select a finance tool to configure</h3>
          <p className="text-sm text-gray-400 mt-1">Choose one of the integrations above to get started</p>
        </div>
      )}
    </div>
  );
}
